import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDrawingRequests,
  getProjectByProperty,
  createDrawingRequest,
  revertDrawingRequest,
  markDrawingRequestAsFinal,
  unmarkDrawingRequestAsFinal,
} from "../../api/drawing-requests";
import { CreateDrawingRequestDto, DrawingRequest, ProjectSimpleDto } from "../../types";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";

const DRAWING_REQUESTS_QUERY_KEY = "drawing-requests";

export function useGetDrawingRequests({
  propertyUid,
}: {
  propertyUid: string;
}) {
  const query = useQuery({
    queryKey: [DRAWING_REQUESTS_QUERY_KEY, propertyUid],
    queryFn: () => getDrawingRequests(propertyUid),
    retry: false,
    enabled: !!propertyUid,
    refetchOnWindowFocus: false,
  });

  return { ...query, data: query.data?.data };
}

export function useGetProjectByProperty({
  propertyUid,
}: {
  propertyUid: string;
}) {
  const query = useQuery<{ data: ProjectSimpleDto }, unknown>({
    queryKey: ["property-project", propertyUid],
    queryFn: () => getProjectByProperty(propertyUid),
    retry: false,
    enabled: !!propertyUid,
    refetchOnWindowFocus: false,
  });
  return { ...query, data: query.data?.data };
}

export function useMarkDrawingRequestAsFinal({
  propertyUid,
}: {
  propertyUid: string;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    { data: DrawingRequest },
    { response?: { data?: { message?: string } } },
    string
  >({
    mutationFn: (requestUid: string) => markDrawingRequestAsFinal(requestUid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DRAWING_REQUESTS_QUERY_KEY, propertyUid],
      });
    },
  });
}

export function useUnmarkDrawingRequestAsFinal({
  propertyUid,
}: {
  propertyUid: string;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    { data: DrawingRequest },
    { response?: { data?: { message?: string } } },
    string
  >({
    mutationFn: (requestUid: string) => unmarkDrawingRequestAsFinal(requestUid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DRAWING_REQUESTS_QUERY_KEY, propertyUid],
      });
    },
  });
}

export function useCreateDrawingRequest({
  propertyUid,
}: {
  propertyUid: string;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    { data: DrawingRequest[] },
    { response?: { data?: { message?: string } } },
    CreateDrawingRequestDto
  >({
    mutationFn: (data: CreateDrawingRequestDto) =>
      createDrawingRequest(propertyUid, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DRAWING_REQUESTS_QUERY_KEY, propertyUid],
      });
      showToast({
        type: ToastType.SUCCESS,
        message: "Drawing request created successfully.",
        toastId: TOAST_IDS.CREATE_DRAWING_REQUEST_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      showToast({
        type: ToastType.ERROR,
        message:
          error?.response?.data?.message ??
          "Failed to create drawing request. Please try again.",
        toastId: TOAST_IDS.CREATE_DRAWING_REQUEST_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });
}

export function useRevertDrawingRequest({
  propertyUid,
}: {
  propertyUid: string;
}) {
  const queryClient = useQueryClient();

  return useMutation<
    { data: DrawingRequest },
    { response?: { data?: { message?: string } } },
    string
  >({
    mutationFn: (uid: string) => revertDrawingRequest(uid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DRAWING_REQUESTS_QUERY_KEY, propertyUid],
      });
      showToast({
        type: ToastType.SUCCESS,
        message: "Drawing request returned to in progress.",
        toastId: TOAST_IDS.REVERT_DRAWING_REQUEST_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      showToast({
        type: ToastType.ERROR,
        message:
          error?.response?.data?.message ??
          "Failed to return drawing request to in progress.",
        toastId: TOAST_IDS.REVERT_DRAWING_REQUEST_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });
}
